import React, { useState, useRef, useEffect } from 'react';
import { useParticipante } from '../../../contexts/ParticipanteContext';
import { validarCPF, validarTelefone, validarEmail, validarArquivo, formatarCPF, formatarTelefone } from '../../../utils/validation';
import { sanitizarInput } from '../../../utils/sanitization';
import { useContasBancarias } from '../../../contexts/ContasBancariasContext';
import { ContaBancaria } from '../../../mocks/contasBancarias';
import { useToast } from '../../../hooks/useToast';
import ToastContainer from '../../../components/base/ToastContainer';
import DocumentosTab from './components/DocumentosTab';
import FotoPerfilModal from './components/FotoPerfilModal';
import AlterarSenhaModal from './components/AlterarSenhaModal';
import {
  get2FAStatus,
  enable2FA,
  confirmEnable2FA,
  change2FAMethod,
  disable2FA,
  getTokenConfig,
  updateTokenConfig,
  getContactChannels,
  updateContactChannels,
  getPrivacy,
  updatePrivacy,
  type TwoFactorStatusResponse,
  type TokenConfigResponse,
  type ContactChannelsResponse,
  type PrivacyResponse,
} from '@/services/accountSettings';
import { ApiException } from '@/utils/errorHandler';
import { buscarCep } from '@/services/cep';

export default function PerfilPage() {
  const { participante } = useParticipante();
  const { contas, definirPrincipal, adicionarConta, removerConta } = useContasBancarias();
  const toast = useToast();
  const [abaAtiva, setAbaAtiva] = useState<'dados' | 'bancarios' | 'documentos' | 'contratos'>('dados');
  const [editando, setEditando] = useState(false);
  const [showAddContaModal, setShowAddContaModal] = useState(false);
  const [showUploadDocModal, setShowUploadDocModal] = useState(false);
  const [uploadDocType, setUploadDocType] = useState<'cpf' | 'rg' | 'cnh' | 'comprovante' | null>(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraDocType, setCameraDocType] = useState<{ tipo: 'cpf' | 'rg' | 'cnh' | 'comprovante', lado?: 'frente' | 'verso' } | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState<'cpf-rg' | 'cnh'>('cpf-rg');
  
  // Novos estados para edição
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [showEditTelefoneModal, setShowEditTelefoneModal] = useState(false);
  const [showChangeFavoritoModal, setShowChangeFavoritoModal] = useState(false);
  const [showEdit2FAModal, setShowEdit2FAModal] = useState(false);
  const [showTelefoneSuccessModal, setShowTelefoneSuccessModal] = useState(false);
  const [showFotoModal, setShowFotoModal] = useState(false);
  const [showAlterarSenhaModal, setShowAlterarSenhaModal] = useState(false);
  const [fotoAtual, setFotoAtual] = useState(participante?.foto || 'https://i.pravatar.cc/150?img=12');
  const [novoEmail, setNovoEmail] = useState('');
  const [novoTelefone, setNovoTelefone] = useState('');
  const [codigoVerificacao, setCodigoVerificacao] = useState('');
  const [emailSelecionado, setEmailSelecionado] = useState('');
  const [etapaVerificacao, setEtapaVerificacao] = useState<'input' | 'codigo'>('input');
  const [editandoCampos, setEditandoCampos] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [erroCEP, setErroCEP] = useState('');

  // Estados para 2FA
  const [autenticacao2FA, setAutenticacao2FA] = useState({
    tokenEmail: true,
    tokenSMS: false
  });
  const [autenticacao2FATemp, setAutenticacao2FATemp] = useState({ ...autenticacao2FA });
  
  // Estado para token
  const [tokenConfig, setTokenConfig] = useState<TokenConfigResponse>({ email: true, sms: false });
  
  // Estado para contato
  const [preferenciasContato, setPreferenciasContato] = useState<ContactChannelsResponse>({ email: true, sms: false, push: true, whatsapp: false });
  
  // Estados para erros de validação
  const [erros, setErros] = useState<Record<string, string>>({});
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [documentos, setDocumentos] = useState({
    cpf: {
      frente: null as string | null,
      verso: null as string | null,
      status: 'pendente' as 'pendente' | 'aprovado' | 'rejeitado',
      dataEnvio: null as string | null,
      motivoRejeicao: null as string | null
    },
    rg: {
      frente: null as string | null,
      verso: null as string | null,
      status: 'pendente' as 'pendente' | 'aprovado' | 'rejeitado',
      dataEnvio: null as string | null,
      motivoRejeicao: null as string | null
    },
    cnh: {
      frente: null as string | null,
      verso: null as string | null,
      status: 'pendente' as 'pendente' | 'aprovado' | 'rejeitado',
      dataEnvio: null as string | null,
      motivoRejeicao: null as string | null
    },
    comprovante: {
      arquivo: null as string | null,
      status: 'pendente' as 'pendente' | 'aprovado' | 'rejeitado',
      dataEnvio: null as string | null,
      motivoRejeicao: null as string | null
    }
  });

  const [showRemoverContaModal, setShowRemoverContaModal] = useState(false);
  const [contaParaRemover, setContaParaRemover] = useState<number | null>(null);
  const [removendoConta, setRemovendoConta] = useState(false);
  const [showRemocaoSucesso, setShowRemocaoSucesso] = useState(false);
  const [adicionandoConta, setAdicionandoConta] = useState(false);
  const [showCadastroSucesso, setShowCadastroSucesso] = useState(false);

  const [dadosPessoais, setDadosPessoais] = useState({
    nomeCompleto: participante?.nome || 'Carlos Eduardo Silva',
    nomeMae: 'Maria Aparecida Silva',
    cpf: '123.456.789-00',
    rg: '12.345.678-9',
    telefone: '(11) 99999-8888',
    cargo: participante?.cargo || 'Consultor de Vendas',
    departamento: participante?.departamento || 'Comercial',
    emails: [
      { email: 'carlos.silva@empresa.com', favorito: true },
      { email: 'carlos.pessoal@gmail.com', favorito: false }
    ],
    endereco: {
      cep: '01310-100',
      logradouro: 'Avenida Paulista',
      numero: '1000',
      complemento: 'Apto 42',
      bairro: 'Bela Vista',
      cidade: 'São Paulo',
      estado: 'SP'
    }
  });

  const [dadosTemp, setDadosTemp] = useState({ ...dadosPessoais });

  const [novaConta, setNovaConta] = useState({
    banco: '',
    agencia: '',
    conta: '',
    tipo: 'Conta Corrente',
    chavePix: '',
    tipoCadastro: '' as string
  });

  const [errosNovaConta, setErrosNovaConta] = useState<Record<string, string>>({});

  const nivelCores = {
    Bronze: 'from-amber-700 to-amber-900',
    Prata: 'from-gray-400 to-gray-600',
    Ouro: 'from-yellow-400 to-yellow-600',
    Platina: 'from-cyan-400 to-cyan-600',
    Diamante: 'from-blue-400 to-purple-600'
  };

  const bancos = [
    'Banco do Brasil', 'Bradesco', 'Caixa Econômica Federal', 'Itaú', 'Santander',
    'Nubank', 'Inter', 'C6 Bank', 'PagBank', 'Banco Original', 'Sicoob', 'Sicredi'
  ];

  // ─── Carregar configurações da API ───
  useEffect(() => {
    async function loadSettings() {
      try {
        const [faRes, tokenRes, contactRes, privacyRes] = await Promise.all([
          get2FAStatus(),
          getTokenConfig(),
          getContactChannels(),
          getPrivacy(),
        ]);

        setAutenticacao2FA({
          tokenEmail: faRes.metodo === 'email',
          tokenSMS: faRes.metodo === 'sms',
        });
        setAutenticacao2FATemp({
          tokenEmail: faRes.metodo === 'email',
          tokenSMS: faRes.metodo === 'sms',
        });

        setTokenConfig(tokenRes);
        setPreferenciasContato(contactRes);
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao carregar configurações';
        toast.error('Erro ao carregar configurações', msg);
      }
    }
    loadSettings();
  }, []);

  const handleAdicionarConta = () => {
    const novosErros: Record<string, string> = {};

    if (!novaConta.tipoCadastro) {
      novosErros.tipoCadastro = 'Selecione o tipo de conta para continuar.';
    }

    if (novaConta.tipoCadastro === 'dados_bancarios') {
      if (!novaConta.banco) {
        novosErros.banco = 'Selecione o banco da sua conta.';
      }
      if (!novaConta.agencia.trim()) {
        novosErros.agencia = 'Informe o número da agência.';
      }
      if (!novaConta.conta.trim()) {
        novosErros.conta = 'Informe o número da conta.';
      }
    } else if (novaConta.tipoCadastro) {
      const chave = novaConta.chavePix.trim();
      if (!chave) {
        novosErros.chavePix = 'Este campo é obrigatório.';
      } else if (novaConta.tipoCadastro === 'chave_cpf') {
        const apenasNumeros = chave.replace(/\D/g, '');
        if (apenasNumeros.length !== 11 && apenasNumeros.length !== 14) {
          novosErros.chavePix = 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos.';
        }
      } else if (novaConta.tipoCadastro === 'chave_email') {
        if (!validarEmail(chave)) {
          novosErros.chavePix = 'Informe um endereço de e-mail válido.';
        }
      } else if (novaConta.tipoCadastro === 'chave_celular') {
        const apenasNumeros = chave.replace(/\D/g, '');
        if (apenasNumeros.length < 10 || apenasNumeros.length > 11) {
          novosErros.chavePix = 'Celular inválido. Use o formato (00) 00000-0000.';
        }
      } else if (novaConta.tipoCadastro === 'chave_aleatoria') {
        if (chave.length < 10) {
          novosErros.chavePix = 'Chave inválida. Cole a chave completa gerada pelo seu banco.';
        }
      }
    }

    if (Object.keys(novosErros).length > 0) {
      setErrosNovaConta(novosErros);
      return;
    }

    setErrosNovaConta({});
    setAdicionandoConta(true);

    const tipoPixMap: Record<string, ContaBancaria['tipoPix']> = {
      dados_bancarios: 'cpf',
      chave_cpf: 'cpf',
      chave_email: 'email',
      chave_celular: 'celular',
      chave_aleatoria: 'aleatoria',
    };

    const novaCt = {
      banco: novaConta.banco || 'Conta PIX',
      agencia: novaConta.tipoCadastro === 'dados_bancarios' ? novaConta.agencia : '-',
      conta: novaConta.tipoCadastro === 'dados_bancarios' ? novaConta.conta : '-',
      tipo: novaConta.tipoCadastro === 'dados_bancarios' ? novaConta.tipo : 'Conta PIX',
      titular: dadosPessoais.nomeCompleto,
      cpf: dadosPessoais.cpf,
      chavePix: novaConta.tipoCadastro === 'dados_bancarios' ? dadosPessoais.cpf : novaConta.chavePix,
      tipoPix: tipoPixMap[novaConta.tipoCadastro] ?? 'cpf',
      principal: contas.length === 0,
    };

    setTimeout(() => {
      adicionarConta(novaCt);
      setAdicionandoConta(false);
      setShowAddContaModal(false);
      setNovaConta({
        banco: '',
        agencia: '',
        conta: '',
        tipo: 'Conta Corrente',
        chavePix: '',
        tipoCadastro: ''
      });
      setErrosNovaConta({});
      setShowCadastroSucesso(true);
      setTimeout(() => setShowCadastroSucesso(false), 3500);
    }, 1500);
  };

  const handleDefinirPrincipal = (id: number) => {
    definirPrincipal(id);
  };

  const handleRemoverConta = (id: number) => {
    const conta = contas.find(c => c.id === id);
    if (conta?.principal) return;
    setContaParaRemover(id);
    setShowRemoverContaModal(true);
  };

  const handleConfirmarRemocao = () => {
    if (contaParaRemover !== null) {
      setRemovendoConta(true);
      setTimeout(() => {
        removerConta(contaParaRemover);
        setRemovendoConta(false);
        setShowRemoverContaModal(false);
        setContaParaRemover(null);
        setShowRemocaoSucesso(true);
        setTimeout(() => setShowRemocaoSucesso(false), 3500);
      }, 1500);
    }
  };

  const getStatusBadge = (status: 'pendente' | 'aprovado' | 'rejeitado') => {
    const styles = {
      pendente: 'bg-yellow-100 text-yellow-700',
      aprovado: 'bg-green-100 text-green-700',
      rejeitado: 'bg-red-100 text-red-700'
    };
    
    const labels = {
      pendente: 'Pendente',
      aprovado: 'Aprovado',
      rejeitado: 'Rejeitado'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, tipo: 'cpf' | 'rg' | 'cnh' | 'comprovante', lado?: 'frente' | 'verso') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar arquivo usando utilitário
    const tiposPermitidos = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const validacao = validarArquivo(file, tiposPermitidos, 5);
    
    if (!validacao.valido) {
      toast.error(validacao.erro || 'Arquivo inválido', 'Verifique o formato e tamanho do arquivo.');
      return;
    }

    // Simular upload
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      
      setDocumentos(prev => {
        const newDocs = { ...prev };
        
        if (tipo === 'comprovante') {
          newDocs.comprovante = {
            arquivo: result,
            status: 'pendente',
            dataEnvio: new Date().toISOString(),
            motivoRejeicao: null
          };
        } else {
          newDocs[tipo] = {
            ...newDocs[tipo],
            [lado as string]: result,
            status: 'pendente',
            dataEnvio: new Date().toISOString(),
            motivoRejeicao: null
          };
        }
        
        return newDocs;
      });

      setShowUploadDocModal(false);
      setUploadDocType(null);
    };
    
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsCameraActive(true);
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      toast.error('Câmera indisponível', 'Verifique as permissões do navegador e tente novamente.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !cameraDocType) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Define o tamanho do canvas igual ao vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenha o frame atual do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converte para base64
    const photoData = canvas.toDataURL('image/jpeg', 0.9);

    // Salva a foto
    setDocumentos(prev => {
      const newDocs = { ...prev };
      
      if (cameraDocType.tipo === 'comprovante') {
        newDocs.comprovante = {
          arquivo: photoData,
          status: 'pendente',
          dataEnvio: new Date().toISOString(),
          motivoRejeicao: null
        };
      } else {
        newDocs[cameraDocType.tipo] = {
          ...newDocs[cameraDocType.tipo],
          [cameraDocType.lado as string]: photoData,
          status: 'pendente',
          dataEnvio: new Date().toISOString(),
          motivoRejeicao: null
        };
      }
      
      return newDocs;
    });

    // Fecha a câmera
    stopCamera();
    setShowCameraModal(false);
    setCameraDocType(null);
  };

  const openCamera = (tipo: 'cpf' | 'rg' | 'cnh' | 'comprovante', lado?: 'frente' | 'verso') => {
    setCameraDocType({ tipo, lado });
    setShowCameraModal(true);
  };

  const closeCameraModal = () => {
    stopCamera();
    setShowCameraModal(false);
    setCameraDocType(null);
  };

  React.useEffect(() => {
    if (showCameraModal && !isCameraActive) {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [showCameraModal]);

  // ─── Funções para gerenciar 2FA via API ───
  const handleIniciarEdicao2FA = () => {
    setShowEdit2FAModal(true);
    setAutenticacao2FATemp({ ...autenticacao2FA });
  };

  const handleSalvar2FA = async () => {
    const metodo = autenticacao2FATemp.tokenEmail ? 'email' : autenticacao2FATemp.tokenSMS ? 'sms' : 'email';
    try {
      await change2FAMethod(metodo, '123456'); // código simulado para demo
      setAutenticacao2FA({ ...autenticacao2FATemp });
      setShowEdit2FAModal(false);
      toast.success('Autenticação atualizada', 'Suas configurações de 2FA foram salvas.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar 2FA';
      toast.error('Erro ao salvar 2FA', msg);
    }
  };

  const handleToggle2FA = (tipo: 'tokenEmail' | 'tokenSMS') => {
    if (tipo === 'tokenEmail') {
      setAutenticacao2FATemp({
        tokenEmail: true,
        tokenSMS: false
      });
    } else {
      setAutenticacao2FATemp({
        tokenEmail: false,
        tokenSMS: true
      });
    }
  };

  // ─── Funções para gerenciar token via API ───
  const handleToggleToken = async (key: 'email' | 'sms') => {
    const next: TokenConfigResponse = { email: key === 'email', sms: key === 'sms' };
    setTokenConfig(next);
    try {
      await updateTokenConfig(next);
      toast.success('Token atualizado', 'A configuração de token foi salva.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar token';
      toast.error('Erro ao salvar token', msg);
    }
  };

  // ─── Funções para gerenciar contato via API ───
  const handleToggleContato = async (key: keyof ContactChannelsResponse) => {
    const next = { ...preferenciasContato, [key]: !preferenciasContato[key] };
    setPreferenciasContato(next);
    try {
      await updateContactChannels(next);
      toast.success('Preferências de contato atualizadas', 'Suas preferências de contato foram salvas.');
    } catch (err) {
      const msg = err instanceof ApiException ? err.message : 'Erro ao salvar contato';
      toast.error('Erro ao salvar contato', msg);
    }
  };

  // Funções para gerenciar e-mails com validação
  const handleAdicionarEmail = () => {
    const emailSanitizado = sanitizarInput(novoEmail.trim());
    
    if (!emailSanitizado) {
      setErros({ ...erros, novoEmail: 'Por favor, informe o e-mail' });
      return;
    }

    if (!validarEmail(emailSanitizado)) {
      setErros({ ...erros, novoEmail: 'E-mail inválido' });
      return;
    }

    if (dadosPessoais.emails.length >= 3) {
      toast.warning('Limite atingido', 'Você já possui 3 e-mails cadastrados. Remova um antes de adicionar outro.');
      return;
    }

    const emailJaExiste = dadosPessoais.emails.some(e => e.email === emailSanitizado);
    if (emailJaExiste) {
      setErros({ ...erros, novoEmail: 'Este e-mail já está cadastrado' });
      return;
    }

    setDadosPessoais(prev => ({
      ...prev,
      emails: [...prev.emails, { email: emailSanitizado, favorito: false }]
    }));

    setNovoEmail('');
    setErros({});
    setShowAddEmailModal(false);
    toast.success('E-mail adicionado', 'O novo e-mail foi cadastrado com sucesso.');
  };

  const handleRemoverEmail = (email: string) => {
    const emailObj = dadosPessoais.emails.find(e => e.email === email);
    
    if (emailObj?.favorito) {
      toast.warning('Ação não permitida', 'Defina outro e-mail como favorito antes de remover este.');
      return;
    }

    if (dadosPessoais.emails.length === 1) {
      toast.warning('Ação não permitida', 'Você precisa ter pelo menos um e-mail cadastrado.');
      return;
    }

    setDadosPessoais(prev => ({
      ...prev,
      emails: prev.emails.filter(e => e.email !== email)
    }));

    toast.success('E-mail removido', 'O e-mail foi excluído do seu perfil.');
  };

  const handleIniciarMudancaFavorito = (email: string) => {
    setEmailSelecionado(email);
    setShowChangeFavoritoModal(true);
    setEtapaVerificacao('codigo');
    setCodigoVerificacao('');
    
    // Simular envio de código
    const emailFavorito = dadosPessoais.emails.find(e => e.favorito)?.email;
    toast.info('Código enviado', `Verifique sua caixa de entrada em ${emailFavorito}.`);
  };

  const handleConfirmarMudancaFavorito = () => {
    if (codigoVerificacao.length !== 4) {
      toast.error('Código inválido', 'Por favor, informe o código de 4 dígitos.');
      return;
    }

    // Simular validação do código
    if (codigoVerificacao !== '1234') {
      toast.error('Código incorreto', 'O código informado não é válido. Tente novamente.');
      return;
    }

    setDadosPessoais(prev => ({
      ...prev,
      emails: prev.emails.map(e => ({
        ...e,
        favorito: e.email === emailSelecionado
      }))
    }));

    setShowChangeFavoritoModal(false);
    setCodigoVerificacao('');
    setEmailSelecionado('');
    toast.success('E-mail favorito alterado', 'Seu e-mail principal foi atualizado com sucesso.');
  };

  // Funções para gerenciar telefone com validação
  const handleIniciarMudancaTelefone = () => {
    setShowEditTelefoneModal(true);
    setEtapaVerificacao('codigo');
    setCodigoVerificacao('');
    setNovoTelefone('');
    setErros({});
    
    // Simular envio de código (sem alert)
    // O feedback será mostrado dentro do modal
  };

  const handleConfirmarCodigoTelefone = () => {
    if (codigoVerificacao.length !== 4) {
      setErros({ ...erros, codigoVerificacao: 'Por favor, informe o código de 4 dígitos' });
      return;
    }

    // Simular validação do código
    if (codigoVerificacao !== '1234') {
      setErros({ ...erros, codigoVerificacao: 'Código inválido. Tente novamente.' });
      return;
    }

    setEtapaVerificacao('input');
    setCodigoVerificacao('');
    setErros({});
  };

  const handleSalvarNovoTelefone = () => {
    const telefoneSanitizado = sanitizarInput(novoTelefone.trim());
    
    if (!telefoneSanitizado) {
      setErros({ ...erros, novoTelefone: 'Por favor, informe o novo telefone' });
      return;
    }

    if (!validarTelefone(telefoneSanitizado)) {
      setErros({ ...erros, novoTelefone: 'Telefone inválido. Use o formato (00) 00000-0000' });
      return;
    }

    setDadosPessoais(prev => ({
      ...prev,
      telefone: formatarTelefone(telefoneSanitizado)
    }));

    setShowEditTelefoneModal(false);
    setNovoTelefone('');
    setEtapaVerificacao('input');
    setErros({});
    
    // Mostrar modal de sucesso
    setShowTelefoneSuccessModal(true);
  };

  // Funções para editar campos com validação
  const handleIniciarEdicao = () => {
    setEditandoCampos(true);
    setDadosTemp({ ...dadosPessoais });
    setErros({});
  };

  const handleCancelarEdicao = () => {
    setEditandoCampos(false);
    setDadosTemp({ ...dadosPessoais });
    setErros({});
    setErroCEP('');
  };

  const handleSalvarEdicao = () => {
    const novosErros: Record<string, string> = {};

    if (dadosTemp.cargo.trim().length < 2) {
      novosErros.cargo = 'Cargo deve ter pelo menos 2 caracteres';
    }

    if (dadosTemp.departamento.trim().length < 2) {
      novosErros.departamento = 'Departamento deve ter pelo menos 2 caracteres';
    }

    if (dadosTemp.endereco.cep && !dadosTemp.endereco.cep.replace(/\D/g, '').match(/^\d{8}$/)) {
      novosErros.cep = 'CEP inválido';
    }

    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      return;
    }

    setDadosPessoais({
      ...dadosTemp,
      cargo: sanitizarInput(dadosTemp.cargo),
      departamento: sanitizarInput(dadosTemp.departamento),
      endereco: {
        ...dadosTemp.endereco,
        logradouro: sanitizarInput(dadosTemp.endereco.logradouro),
        complemento: sanitizarInput(dadosTemp.endereco.complemento),
        bairro: sanitizarInput(dadosTemp.endereco.bairro),
        cidade: sanitizarInput(dadosTemp.endereco.cidade)
      }
    });

    setEditandoCampos(false);
    setErros({});
    setErroCEP('');
    toast.success('Dados atualizados', 'Suas informações foram salvas com sucesso.');
  };

  const handleCEPChange = async (cep: string) => {
    const cepFormatado = cep
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 9);

    setDadosTemp(prev => ({ ...prev, endereco: { ...prev.endereco, cep: cepFormatado } }));
    setErroCEP('');

    const digits = cepFormatado.replace(/\D/g, '');
    if (digits.length === 8) {
      setBuscandoCEP(true);
      try {
        const data = await buscarCep(digits);
        setDadosTemp(prev => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            cep: cepFormatado,
            logradouro: data.logradouro || prev.endereco.logradouro,
            bairro: data.bairro || prev.endereco.bairro,
            cidade: data.localidade || prev.endereco.cidade,
            estado: data.uf || prev.endereco.estado,
          }
        }));
      } catch (err) {
        const msg = err instanceof ApiException ? err.message : 'Erro ao buscar CEP. Verifique sua conexão.';
        setErroCEP(msg);
      } finally {
        setBuscandoCEP(false);
      }
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6 pb-20 lg:pb-6 px-3 lg:px-0">
      {/* Header do Perfil */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-5 lg:p-8 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="relative group cursor-pointer" onClick={() => setShowFotoModal(true)}>
            <img
              src={fotoAtual}
              alt={participante?.nome}
              className="w-20 h-20 lg:w-24 lg:h-24 rounded-full border-4 border-white/30 object-cover shadow-lg"
            />
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <i className="ri-camera-line text-white text-2xl"></i>
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-orange-200">
              <i className="ri-camera-line text-orange-600 text-sm"></i>
            </div>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">{participante?.nome}</h1>
            <p className="text-sm lg:text-base text-orange-100 mb-3 lg:mb-4">{participante?.cargo} • {participante?.departamento}</p>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 lg:gap-3">
              <span className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-full text-xs lg:text-sm font-bold bg-gradient-to-r ${nivelCores[participante?.nivel || 'Bronze']} shadow-lg`}>
                {participante?.nivel}
              </span>

              <span className="px-3 lg:px-4 py-1.5 lg:py-2 rounded-full text-xs lg:text-sm font-bold bg-white/20 backdrop-blur-sm">
                <i className="ri-trophy-line mr-1 lg:mr-2"></i>
                {participante?.campanhasAtivas} Campanhas
              </span>
            </div>
          </div>
        </div>
      </div>



      {/* Abas */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200">
          <div className="flex overflow-x-auto scrollbar-hide">
            {[
              { id: 'dados', label: 'Dados Pessoais', icon: 'ri-user-line' },
              { id: 'bancarios', label: 'Dados Bancários', icon: 'ri-bank-card-line' },
              { id: 'documentos', label: 'Documentos', icon: 'ri-file-text-line' },
              { id: 'contratos', label: 'Contratos', icon: 'ri-file-list-line' }
            ].map((aba) => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id as any)}
                className={`px-4 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm font-semibold whitespace-nowrap transition-colors ${
                  abaAtiva === aba.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <i className={`${aba.icon} mr-1 lg:mr-2`}></i>
                {aba.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 lg:p-6">
          {/* Dados Pessoais */}
          {abaAtiva === 'dados' && (
            <div className="space-y-5 lg:space-y-6">
              {/* Botão de Editar */}
              <div className="flex justify-end">
                {!editandoCampos ? (
                  <button
                    onClick={handleIniciarEdicao}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap flex items-center gap-2"
                  >
                    <i className="ri-edit-line"></i>
                    Editar Dados
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelarEdicao}
                      className="px-3 lg:px-4 py-2 bg-white border-2 border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors whitespace-nowrap"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSalvarEdicao}
                      className="px-3 lg:px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-save-line"></i>
                      Salvar
                    </button>
                  </div>
                )}
              </div>

              {/* Informações Básicas */}
              <div>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-user-line text-orange-600"></i>
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 gap-4 lg:gap-5">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Nome Completo</label>
                    <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.nomeCompleto}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Nome da Mãe</label>
                    <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.nomeMae}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">CPF</label>
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.cpf}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">RG</label>
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.rg}</p>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Telefone</label>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.telefone}</p>
                      <button
                        onClick={handleIniciarMudancaTelefone}
                        className="text-orange-600 hover:text-orange-700 text-xs lg:text-sm font-semibold whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-edit-line"></i>
                        Alterar
                      </button>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Cargo</label>
                    {editandoCampos ? (
                      <input
                        type="text"
                        value={dadosTemp.cargo}
                        onChange={(e) => setDadosTemp({ ...dadosTemp, cargo: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    ) : (
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.cargo}</p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Departamento</label>
                    {editandoCampos ? (
                      <input
                        type="text"
                        value={dadosTemp.departamento}
                        onChange={(e) => setDadosTemp({ ...dadosTemp, departamento: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    ) : (
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.departamento}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* E-mails */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base lg:text-lg font-bold text-slate-900 flex items-center gap-2">
                    <i className="ri-mail-line text-orange-600"></i>
                    E-mails
                  </h3>
                  {dadosPessoais.emails.length < 3 && (
                    <button
                      onClick={() => setShowAddEmailModal(true)}
                      className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs lg:text-sm font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-add-line"></i>
                      Adicionar
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {dadosPessoais.emails.map((emailItem, index) => (
                    <div key={index} className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-orange-600"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm lg:text-base text-slate-900 font-medium break-all">{emailItem.email}</p>
                          {emailItem.favorito && (
                            <span className="text-xs text-orange-600 font-semibold flex items-center gap-1 mt-1">
                              <i className="ri-star-fill"></i>
                              E-mail Favorito
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        {!emailItem.favorito && (
                          <>
                            <button
                              onClick={() => handleIniciarMudancaFavorito(emailItem.email)}
                              className="px-3 py-1.5 text-orange-600 hover:bg-orange-50 rounded-lg text-xs lg:text-sm font-semibold transition-colors whitespace-nowrap"
                            >
                              <i className="ri-star-line mr-1"></i>
                              Favoritar
                            </button>
                            <button
                              onClick={() => handleRemoverEmail(emailItem.email)}
                              className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-xs lg:text-sm font-semibold transition-colors whitespace-nowrap"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {dadosPessoais.emails.length >= 3 && (
                  <p className="text-xs lg:text-sm text-slate-600 mt-3 flex items-start gap-2">
                    <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                    <span>Você atingiu o limite de 3 e-mails cadastrados</span>
                  </p>
                )}
              </div>

              {/* Endereço */}
              <div>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-map-pin-line text-orange-600"></i>
                  Endereço
                </h3>
                <div className="grid grid-cols-1 gap-4 lg:gap-5">
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">CEP</label>
                      {editandoCampos ? (
                        <div>
                          <div className="relative">
                            <input
                              type="text"
                              value={dadosTemp.endereco.cep}
                              onChange={(e) => handleCEPChange(e.target.value)}
                              placeholder="00000-000"
                              maxLength={9}
                              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm pr-8 ${erroCEP ? 'border-red-400' : 'border-slate-300'}`}
                            />
                            {buscandoCEP && (
                              <div className="absolute inset-y-0 right-2 flex items-center">
                                <i className="ri-loader-4-line animate-spin text-orange-500 text-sm"></i>
                              </div>
                            )}
                          </div>
                          {erroCEP && (
                            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                              <i className="ri-error-warning-line"></i>
                              {erroCEP}
                            </p>
                          )}
                          {!erroCEP && dadosTemp.endereco.cep.replace(/\D/g, '').length === 8 && !buscandoCEP && (
                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                              <i className="ri-check-line"></i>
                              CEP encontrado
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.cep}</p>
                      )}
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Número</label>
                      {editandoCampos ? (
                        <input
                          type="text"
                          value={dadosTemp.endereco.numero}
                          onChange={(e) => setDadosTemp({ ...dadosTemp, endereco: { ...dadosTemp.endereco, numero: e.target.value } })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      ) : (
                        <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.numero}</p>
                      )}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Logradouro</label>
                    {editandoCampos ? (
                      <input
                        type="text"
                        value={dadosTemp.endereco.logradouro}
                        onChange={(e) => setDadosTemp({ ...dadosTemp, endereco: { ...dadosTemp.endereco, logradouro: e.target.value } })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    ) : (
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.logradouro}</p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Complemento</label>
                    {editandoCampos ? (
                      <input
                        type="text"
                        value={dadosTemp.endereco.complemento}
                        onChange={(e) => setDadosTemp({ ...dadosTemp, endereco: { ...dadosTemp.endereco, complemento: e.target.value } })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    ) : (
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.complemento || '-'}</p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Bairro</label>
                    {editandoCampos ? (
                      <input
                        type="text"
                        value={dadosTemp.endereco.bairro}
                        onChange={(e) => setDadosTemp({ ...dadosTemp, endereco: { ...dadosTemp.endereco, bairro: e.target.value } })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      />
                    ) : (
                      <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.bairro}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Cidade</label>
                      {editandoCampos ? (
                        <input
                          type="text"
                          value={dadosTemp.endereco.cidade}
                          onChange={(e) => setDadosTemp({ ...dadosTemp, endereco: { ...dadosTemp.endereco, cidade: e.target.value } })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        />
                      ) : (
                        <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.cidade}</p>
                      )}
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <label className="text-xs lg:text-sm font-semibold text-slate-700 mb-2 block">Estado</label>
                      {editandoCampos ? (
                        <input
                          type="text"
                          value={dadosTemp.endereco.estado}
                          onChange={(e) => setDadosTemp({ ...dadosTemp, endereco: { ...dadosTemp.endereco, estado: e.target.value } })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                          maxLength={2}
                        />
                      ) : (
                        <p className="text-sm lg:text-base text-slate-900">{dadosPessoais.endereco.estado}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuração de Token */}
              <div>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-key-2-line text-orange-600"></i>
                  Configuração de Token
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-3">
                  {(Object.entries({ email: 'Token por E-mail', sms: 'Token por SMS' }) as [keyof TokenConfigResponse, string][]).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${key === 'email' ? 'bg-blue-100' : 'bg-green-100'}`}>
                          <i className={`${key === 'email' ? 'ri-mail-line text-blue-600' : 'ri-message-3-line text-green-600'}`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                          <p className="text-xs text-slate-500">Receber código via {key === 'email' ? 'e-mail' : 'SMS'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleToken(key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${tokenConfig[key] ? 'bg-orange-600' : 'bg-slate-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${tokenConfig[key] ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alterar Senha */}
              <div>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-lock-password-line text-orange-600"></i>
                  Senha de Acesso
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                        <i className="ri-lock-line text-slate-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Senha atual</p>
                        <p className="text-xs text-slate-500">••••••••••••</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAlterarSenhaModal(true)}
                      className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs lg:text-sm font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-edit-line"></i>
                      Alterar Senha
                    </button>
                  </div>
                </div>
              </div>

              {/* Autenticação de Dois Fatores */}
              <div>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-shield-check-line text-orange-600"></i>
                  Autenticação de Dois Fatores
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 mb-1">Métodos de Verificação</p>
                      <p className="text-xs text-slate-600">Escolha como deseja receber códigos de verificação</p>
                    </div>
                    <button
                      onClick={handleIniciarEdicao2FA}
                      className="text-orange-600 hover:text-orange-700 text-xs lg:text-sm font-semibold whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-edit-line"></i>
                      Alterar
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <i className="ri-mail-line text-blue-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Token por E-mail</p>
                          <p className="text-xs text-slate-600">Receber código via e-mail</p>
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        autenticacao2FA.tokenEmail ? 'bg-orange-600' : 'bg-slate-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mt-0.5 ${
                          autenticacao2FA.tokenEmail ? 'translate-x-6' : 'translate-x-0.5'
                        }`}></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <i className="ri-message-3-line text-green-600"></i>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Token por SMS</p>
                          <p className="text-xs text-slate-600">Receber código via SMS</p>
                        </div>
                      </div>
                      <div className={`w-12 h-6 rounded-full transition-colors ${
                        autenticacao2FA.tokenSMS ? 'bg-orange-600' : 'bg-slate-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform mt-0.5 ${
                          autenticacao2FA.tokenSMS ? 'translate-x-6' : 'translate-x-0.5'
                        }`}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}





          {/* Dados Bancários */}
          {abaAtiva === 'bancarios' && (
            <div className="space-y-5 lg:space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <h3 className="text-base lg:text-lg font-bold text-slate-900">Contas Bancárias</h3>
                  <p className="text-xs lg:text-sm text-slate-600 mt-1">Gerencie suas contas para resgates via PIX</p>
                </div>
                <button
                  onClick={() => setShowAddContaModal(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap w-full lg:w-auto"
                >
                  <i className="ri-add-line mr-2"></i>
                  Nova Conta
                </button>
              </div>

              {contas.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-bank-card-line text-3xl text-slate-400"></i>
                  </div>
                  <p className="text-sm lg:text-base text-slate-600 mb-4">Nenhuma conta bancária cadastrada</p>
                  <button
                    onClick={() => setShowAddContaModal(true)}
                    className="px-6 py-3 bg-orange-600 text-white rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-2"></i>
                    Cadastrar Primeira Conta
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {contas.map((conta) => {
                    const isDadosBancarios = conta.tipo !== 'Conta PIX';
                    const tipoIcone = isDadosBancarios ? 'ri-bank-line' : 'ri-exchange-funds-line';
                    const tipoLabel = isDadosBancarios ? conta.tipo : `PIX · ${conta.tipoPix}`;

                    return (
                      <div
                        key={conta.id}
                        className={`rounded-xl border-2 transition-all overflow-hidden ${
                          conta.principal
                            ? 'border-orange-400 bg-white shadow-md'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                        }`}
                      >
                        {/* Cabeçalho do card */}
                        <div className={`flex items-center justify-between px-5 py-4 ${conta.principal ? 'bg-orange-50' : 'bg-slate-50'} border-b ${conta.principal ? 'border-orange-200' : 'border-slate-200'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${conta.principal ? 'bg-orange-100' : 'bg-white border border-slate-200'}`}>
                              <i className={`${tipoIcone} text-lg ${conta.principal ? 'text-orange-600' : 'text-slate-500'}`}></i>
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-900">{conta.banco !== '-' ? conta.banco : 'Conta PIX'}</p>
                              <p className={`text-xs font-medium ${conta.principal ? 'text-orange-600' : 'text-slate-500'}`}>{tipoLabel}</p>
                            </div>
                          </div>
                          {conta.principal && (
                            <span className="px-2.5 py-1 bg-orange-600 text-white text-[10px] font-bold rounded-full whitespace-nowrap flex items-center gap-1">
                              <i className="ri-star-fill"></i>
                              Principal
                            </span>
                          )}
                        </div>

                        {/* Corpo do card */}
                        <div className="px-5 py-4 space-y-3">
                          {/* Titular sempre visível */}
                          <div className="flex items-center gap-2">
                            <i className="ri-user-line text-slate-400 text-sm flex-shrink-0"></i>
                            <div>
                              <p className="text-[10px] text-slate-500 leading-none mb-0.5">Titular</p>
                              <p className="text-sm font-semibold text-slate-900">{conta.titular}</p>
                            </div>
                            <span className="ml-auto text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{conta.cpf}</span>
                          </div>

                          <div className="border-t border-slate-100"></div>

                          {/* Dados Bancários */}
                          {isDadosBancarios && (
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                                <p className="text-[10px] text-slate-500 mb-0.5">Agência</p>
                                <p className="text-sm font-semibold text-slate-900">{conta.agencia}</p>
                              </div>
                              <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100">
                                <p className="text-[10px] text-slate-500 mb-0.5">Conta</p>
                                <p className="text-sm font-semibold text-slate-900">{conta.conta}</p>
                              </div>
                            </div>
                          )}

                          {/* Chave PIX */}
                          {!isDadosBancarios && (
                            <div className="bg-slate-50 rounded-lg px-3 py-2.5 border border-slate-100 flex items-center gap-2">
                              <i className="ri-key-line text-orange-400 text-base flex-shrink-0"></i>
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] text-slate-500 mb-0.5">Chave PIX · {conta.tipoPix}</p>
                                <p className="text-sm font-semibold text-slate-900 break-all">{conta.chavePix}</p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Rodapé com ações */}
                        <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50/50">
                          {!conta.principal && (
                            <button
                              onClick={() => handleDefinirPrincipal(conta.id)}
                              className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-lg text-xs font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap flex items-center justify-center gap-1.5"
                            >
                              <i className="ri-star-line"></i>
                              Definir como Principal
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoverConta(conta.id)}
                            className={`px-3 py-2 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 transition-colors whitespace-nowrap flex items-center gap-1.5 ${conta.principal ? 'flex-1 justify-center' : ''}`}
                          >
                            <i className="ri-delete-bin-line"></i>
                            Remover
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-lg lg:text-xl text-blue-600 mt-0.5 flex-shrink-0"></i>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs lg:text-sm font-semibold text-blue-900 mb-2">Informações Importantes</p>
                    <ul className="text-xs lg:text-sm text-blue-800 space-y-1.5">
                      <li>• A conta principal será utilizada para todos os resgates via PIX</li>
                      <li>• Você pode alterar a conta principal a qualquer momento</li>
                      <li>• Certifique-se de que os dados estão corretos antes de solicitar resgates</li>
                      <li>• O CPF do titular deve ser o mesmo do cadastro na plataforma</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aba Documentos */}
          {abaAtiva === 'documentos' && (
            <DocumentosTab
              tipoDocumento={tipoDocumento}
              setTipoDocumento={setTipoDocumento}
              documentos={documentos}
              setDocumentos={setDocumentos}
            />
          )}

          {/* Aba Contratos */}
          {abaAtiva === 'contratos' && (
            <div className="space-y-5 lg:space-y-6">
              <div>
                <h3 className="text-base lg:text-lg font-bold text-slate-900 mb-2">Contratos e Termos</h3>
                <p className="text-xs lg:text-sm text-slate-600">
                  Visualize e gerencie seus contratos e termos aceitos
                </p>
              </div>

              <div className="space-y-3 lg:space-y-4">
                {[
                  {
                    id: 1,
                    titulo: 'Termo de Adesão ao Programa',
                    data: '15/01/2024',
                    status: 'Ativo',
                    arquivo: 'termo-adesao.pdf'
                  },
                  {
                    id: 2,
                    titulo: 'Política de Privacidade',
                    data: '15/01/2024',
                    status: 'Ativo',
                    arquivo: 'politica-privacidade.pdf'
                  },
                  {
                    id: 3,
                    titulo: 'Regulamento do Programa de Incentivo',
                    data: '15/01/2024',
                    status: 'Ativo',
                    arquivo: 'regulamento.pdf'
                  }
                ].map((contrato) => (
                  <div key={contrato.id} className="bg-white border border-slate-200 rounded-xl p-4 lg:p-6 hover:shadow-md transition-shadow">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex items-start gap-3 lg:gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-xl lg:text-2xl text-orange-600"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-sm lg:text-base text-slate-900 mb-2">{contrato.titulo}</h4>
                          <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4 text-xs lg:text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <i className="ri-calendar-line"></i>
                              Aceito em: {contrato.data}
                            </span>
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold inline-block w-fit">
                              {contrato.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button className="px-4 py-2.5 text-sm font-semibold text-orange-600 hover:bg-orange-50 rounded-lg transition-colors whitespace-nowrap flex items-center justify-center gap-2 w-full lg:w-auto">
                        <i className="ri-download-line"></i>
                        Baixar PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <i className="ri-information-line text-slate-600 text-lg lg:text-xl flex-shrink-0 mt-0.5"></i>
                  <div className="text-xs lg:text-sm text-slate-700 min-w-0">
                    <p className="font-semibold mb-1">Sobre os contratos:</p>
                    <p>Você pode visualizar e baixar todos os contratos e termos que aceitou ao participar do programa. Estes documentos ficam disponíveis para consulta a qualquer momento.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modais serão adicionados aqui - código muito extenso */}
      {/* Modal Adicionar E-mail */}
      {showAddEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900">Adicionar E-mail</h3>
              <button
                onClick={() => {
                  setShowAddEmailModal(false);
                  setNovoEmail('');
                  setErros({});
                }}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">Novo E-mail</label>
                <input
                  type="email"
                  value={novoEmail}
                  onChange={(e) => {
                    setNovoEmail(e.target.value);
                    setErros({});
                  }}
                  placeholder="seu.email@exemplo.com"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    erros.novoEmail ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                {erros.novoEmail && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {erros.novoEmail}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <i className="ri-information-line mr-1"></i>
                  Um código de verificação será enviado para este e-mail
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowAddEmailModal(false);
                    setNovoEmail('');
                    setErros({});
                  }}
                  className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdicionarEmail}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Telefone */}
      {showEditTelefoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900">Alterar Telefone</h3>
              <button
                onClick={() => {
                  setShowEditTelefoneModal(false);
                  setNovoTelefone('');
                  setCodigoVerificacao('');
                  setEtapaVerificacao('input');
                  setErros({});
                }}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {etapaVerificacao === 'codigo' && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <i className="ri-message-3-line text-3xl text-blue-600 mb-2"></i>
                    <p className="text-sm text-blue-900 font-semibold mb-1">Código enviado!</p>
                    <p className="text-xs text-blue-800">
                      Digite o código de 4 dígitos enviado para {dadosPessoais.telefone}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">Código de Verificação</label>
                    <input
                      type="text"
                      value={codigoVerificacao}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setCodigoVerificacao(valor);
                        setErros({});
                      }}
                      placeholder="0000"
                      maxLength={4}
                      className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        erros.codigoVerificacao ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {erros.codigoVerificacao && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {erros.codigoVerificacao}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleConfirmarCodigoTelefone}
                    className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    Confirmar Código
                  </button>
                </>
              )}

              {etapaVerificacao === 'input' && (
                <>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">Novo Telefone</label>
                    <input
                      type="tel"
                      value={novoTelefone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        let formatted = value;
                        
                        if (value.length <= 11) {
                          if (value.length <= 2) {
                            formatted = value;
                          } else if (value.length <= 6) {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2)}`;
                          } else if (value.length <= 10) {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
                          } else {
                            formatted = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
                          }
                        }
                        
                        setNovoTelefone(formatted);
                        setErros({});
                      }}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        erros.novoTelefone ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {erros.novoTelefone && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {erros.novoTelefone}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowEditTelefoneModal(false);
                        setNovoTelefone('');
                        setEtapaVerificacao('input');
                        setErros({});
                      }}
                      className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSalvarNovoTelefone}
                      className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                    >
                      Salvar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Mudar E-mail Favorito */}
      {showChangeFavoritoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900">Alterar E-mail Favorito</h3>
              <button
                onClick={() => {
                  setShowChangeFavoritoModal(false);
                  setCodigoVerificacao('');
                  setEmailSelecionado('');
                }}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <i className="ri-mail-line text-3xl text-blue-600 mb-2"></i>
                <p className="text-sm text-blue-900 font-semibold mb-1">Código enviado!</p>
                <p className="text-xs text-blue-800">
                  Digite o código enviado para seu e-mail favorito atual
                </p>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">Código de Verificação</label>
                <input
                  type="text"
                  value={codigoVerificacao}
                  onChange={(e) => {
                    const valor = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setCodigoVerificacao(valor);
                  }}
                  placeholder="0000"
                  maxLength={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-xs text-slate-600 mb-2">Novo e-mail favorito:</p>
                <p className="text-sm font-semibold text-slate-900 break-all">{emailSelecionado}</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowChangeFavoritoModal(false);
                    setCodigoVerificacao('');
                    setEmailSelecionado('');
                  }}
                  className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarMudancaFavorito}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar 2FA */}
      {showEdit2FAModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg lg:text-xl font-bold text-slate-900">Autenticação de Dois Fatores</h3>
              <button
                onClick={() => {
                  setShowEdit2FAModal(false);
                  setAutenticacao2FATemp({ ...autenticacao2FA });
                }}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                Escolha como deseja receber os códigos de verificação. Apenas um método pode estar ativo por vez.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => handleToggle2FA('tokenEmail')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    autenticacao2FATemp.tokenEmail
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      autenticacao2FATemp.tokenEmail ? 'bg-blue-100' : 'bg-slate-100'
                    }`}>
                      <i className={`ri-mail-line text-2xl ${
                        autenticacao2FATemp.tokenEmail ? 'text-blue-600' : 'text-slate-400'
                      }`}></i>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-900">Token por E-mail</p>
                      <p className="text-xs text-slate-600">Receber código via e-mail</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    autenticacao2FATemp.tokenEmail
                      ? 'border-orange-600 bg-orange-600'
                      : 'border-slate-300'
                  }`}>
                    {autenticacao2FATemp.tokenEmail && (
                      <i className="ri-check-line text-white text-sm"></i>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => handleToggle2FA('tokenSMS')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    autenticacao2FATemp.tokenSMS
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      autenticacao2FATemp.tokenSMS ? 'bg-green-100' : 'bg-slate-100'
                    }`}>
                      <i className={`ri-message-3-line text-2xl ${
                        autenticacao2FATemp.tokenSMS ? 'text-green-600' : 'text-slate-400'
                      }`}></i>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-900">Token por SMS</p>
                      <p className="text-xs text-slate-600">Receber código via SMS</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    autenticacao2FATemp.tokenSMS
                      ? 'border-orange-600 bg-orange-600'
                      : 'border-slate-300'
                  }`}>
                    {autenticacao2FATemp.tokenSMS && (
                      <i className="ri-check-line text-white text-sm"></i>
                    )}
                  </div>
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <i className="ri-information-line mr-1"></i>
                  A autenticação de dois fatores adiciona uma camada extra de segurança à sua conta
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowEdit2FAModal(false);
                    setAutenticacao2FATemp({ ...autenticacao2FA });
                  }}
                  className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvar2FA}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Conta Bancária */}
      {showAddContaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl my-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-5 rounded-t-2xl relative">
              <button
                onClick={() => {
                  setShowAddContaModal(false);
                  setNovaConta({ banco: '', agencia: '', conta: '', tipo: 'Conta Corrente', chavePix: '', tipoCadastro: '' });
                  setErrosNovaConta({});
                }}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-bank-card-line text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold">Nova Conta Bancária</h3>
                  <p className="text-xs opacity-90">Cadastre uma conta para resgates</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Titular (somente leitura) */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">Titular da Conta</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-orange-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{dadosPessoais.nomeCompleto}</p>
                    <p className="text-xs text-slate-600">CPF: {dadosPessoais.cpf}</p>
                  </div>
                </div>
                <p className="text-[10px] text-orange-600 mt-2 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  O titular é sempre o participante logado na plataforma
                </p>
              </div>

              {/* Tipo de Conta */}
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-3 block">Tipo de Conta *</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { id: 'dados_bancarios', label: 'Dados Bancários', sub: 'Agência + Conta corrente/poupança', icon: 'ri-bank-line' },
                    { id: 'chave_cpf', label: 'Chave CPF/CNPJ', sub: 'Usar CPF ou CNPJ como chave PIX', icon: 'ri-user-line' },
                    { id: 'chave_email', label: 'Chave E-mail', sub: 'Usar e-mail como chave PIX', icon: 'ri-mail-line' },
                    { id: 'chave_celular', label: 'Chave Celular', sub: 'Usar número de celular como chave PIX', icon: 'ri-smartphone-line' },
                    { id: 'chave_aleatoria', label: 'Chave Aleatória', sub: 'Chave PIX gerada automaticamente', icon: 'ri-key-line' },
                  ].map((tipo) => (
                    <button
                      key={tipo.id}
                      type="button"
                      onClick={() => {
                        setNovaConta({ ...novaConta, tipoCadastro: tipo.id as any, chavePix: '', agencia: '', conta: '' });
                        setErrosNovaConta({});
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        novaConta.tipoCadastro === tipo.id
                          ? 'border-orange-500 bg-orange-50'
                          : errosNovaConta.tipoCadastro
                          ? 'border-red-300 bg-red-50/30 hover:border-red-400'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        novaConta.tipoCadastro === tipo.id ? 'bg-orange-100' : 'bg-slate-100'
                      }`}>
                        <i className={`${tipo.icon} text-xl ${
                          novaConta.tipoCadastro === tipo.id ? 'text-orange-600' : 'text-slate-500'
                        }`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${novaConta.tipoCadastro === tipo.id ? 'text-orange-700' : 'text-slate-800'}`}>{tipo.label}</p>
                        <p className="text-xs text-slate-500 truncate">{tipo.sub}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        novaConta.tipoCadastro === tipo.id ? 'border-orange-500 bg-orange-500' : 'border-slate-300'
                      }`}>
                        {novaConta.tipoCadastro === tipo.id && <i className="ri-check-line text-white text-[10px]"></i>}
                      </div>
                    </button>
                  ))}
                </div>
                {errosNovaConta.tipoCadastro && (
                  <div className="mt-2 flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-fill text-red-500"></i>
                    <p className="text-xs text-red-700 font-medium">{errosNovaConta.tipoCadastro}</p>
                  </div>
                )}
              </div>

              {/* Campos dinâmicos conforme tipo */}
              {novaConta.tipoCadastro === 'dados_bancarios' && (
                <div className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Dados Bancários</p>
                  {/* Banco */}
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Banco *</label>
                    <select
                      value={novaConta.banco}
                      onChange={(e) => {
                        setNovaConta({ ...novaConta, banco: e.target.value });
                        setErrosNovaConta(prev => ({ ...prev, banco: '' }));
                      }}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white cursor-pointer text-sm ${
                        errosNovaConta.banco ? 'border-red-400 bg-red-50' : 'border-slate-300'
                      }`}
                    >
                      <option value="">Selecione o banco</option>
                      {bancos.map((banco) => (
                        <option key={banco} value={banco}>{banco}</option>
                      ))}
                    </select>
                    {errosNovaConta.banco && (
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600 font-medium">
                        <i className="ri-error-warning-fill text-red-500"></i>
                        {errosNovaConta.banco}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Agência *</label>
                      <input
                        type="text"
                        value={novaConta.agencia}
                        onChange={(e) => {
                          setNovaConta({ ...novaConta, agencia: e.target.value });
                          setErrosNovaConta(prev => ({ ...prev, agencia: '' }));
                        }}
                        placeholder="0000-0"
                        className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white ${
                          errosNovaConta.agencia ? 'border-red-400 bg-red-50' : 'border-slate-300'
                        }`}
                      />
                      {errosNovaConta.agencia && (
                        <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600 font-medium">
                          <i className="ri-error-warning-fill text-red-500"></i>
                          {errosNovaConta.agencia}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Conta *</label>
                      <input
                        type="text"
                        value={novaConta.conta}
                        onChange={(e) => {
                          setNovaConta({ ...novaConta, conta: e.target.value });
                          setErrosNovaConta(prev => ({ ...prev, conta: '' }));
                        }}
                        placeholder="00000-0"
                        className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white ${
                          errosNovaConta.conta ? 'border-red-400 bg-red-50' : 'border-slate-300'
                        }`}
                      />
                      {errosNovaConta.conta && (
                        <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600 font-medium">
                          <i className="ri-error-warning-fill text-red-500"></i>
                          {errosNovaConta.conta}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Tipo de Conta</label>
                    <select
                      value={novaConta.tipo}
                      onChange={(e) => setNovaConta({ ...novaConta, tipo: e.target.value })}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white cursor-pointer"
                    >
                      <option value="Conta Corrente">Conta Corrente</option>
                      <option value="Conta Poupança">Conta Poupança</option>
                    </select>
                  </div>
                </div>
              )}

              {novaConta.tipoCadastro && novaConta.tipoCadastro !== 'dados_bancarios' && (
                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                  <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                    {novaConta.tipoCadastro === 'chave_cpf' ? 'CPF / CNPJ *' :
                     novaConta.tipoCadastro === 'chave_email' ? 'E-mail *' :
                     novaConta.tipoCadastro === 'chave_celular' ? 'Celular *' :
                     'Chave Aleatória *'}
                  </label>
                  <input
                    type={novaConta.tipoCadastro === 'chave_email' ? 'email' : 'text'}
                    value={novaConta.chavePix}
                    onChange={(e) => {
                      let valor = e.target.value;

                      if (novaConta.tipoCadastro === 'chave_cpf') {
                        const apenasNumeros = valor.replace(/\D/g, '');
                        if (apenasNumeros.length <= 11) {
                          valor = apenasNumeros
                            .replace(/(\d{3})(\d)/, '$1.$2')
                            .replace(/(\d{3})(\d)/, '$1.$2')
                            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                        } else {
                          valor = apenasNumeros
                            .replace(/(\d{2})(\d)/, '$1.$2')
                            .replace(/(\d{3})(\d)/, '$1.$2')
                            .replace(/(\d{3})(\d)/, '$1/$2')
                            .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
                        }
                      } else if (novaConta.tipoCadastro === 'chave_celular') {
                        const apenasNumeros = valor.replace(/\D/g, '');
                        if (apenasNumeros.length <= 2) {
                          valor = apenasNumeros;
                        } else if (apenasNumeros.length <= 6) {
                          valor = `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2)}`;
                        } else if (apenasNumeros.length <= 10) {
                          valor = `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 6)}-${apenasNumeros.slice(6)}`;
                        } else {
                          valor = `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 7)}-${apenasNumeros.slice(7, 11)}`;
                        }
                      }

                      setNovaConta({ ...novaConta, chavePix: valor });
                      setErrosNovaConta(prev => ({ ...prev, chavePix: '' }));
                    }}
                    placeholder={
                      novaConta.tipoCadastro === 'chave_cpf' ? '000.000.000-00 ou 00.000.000/0000-00' :
                      novaConta.tipoCadastro === 'chave_email' ? 'seu@email.com' :
                      novaConta.tipoCadastro === 'chave_celular' ? '(00) 00000-0000' :
                      'Cole a chave aleatória aqui'
                    }
                    className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm bg-white ${
                      errosNovaConta.chavePix ? 'border-red-400 bg-red-50' : 'border-slate-300'
                    }`}
                  />
                  {errosNovaConta.chavePix ? (
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600 font-medium">
                      <i className="ri-error-warning-fill text-red-500"></i>
                      {errosNovaConta.chavePix}
                    </p>
                  ) : (
                    <p className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      {novaConta.tipoCadastro === 'chave_cpf' && 'Informe o CPF (11 dígitos) ou CNPJ (14 dígitos)'}
                      {novaConta.tipoCadastro === 'chave_email' && 'Informe o e-mail cadastrado como chave PIX'}
                      {novaConta.tipoCadastro === 'chave_celular' && 'Informe o celular com DDD cadastrado como chave PIX'}
                      {novaConta.tipoCadastro === 'chave_aleatoria' && 'Cole a chave aleatória gerada pelo seu banco (formato UUID)'}
                    </p>
                  )}
                </div>
              )}

              {/* Resumo de erros (caso haja múltiplos) */}
              {Object.keys(errosNovaConta).filter(k => errosNovaConta[k]).length > 1 && (
                <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                  <i className="ri-alert-line text-red-500 text-base flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-red-700 font-medium">
                    Preencha todos os campos obrigatórios antes de continuar.
                  </p>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => {
                    setShowAddContaModal(false);
                    setNovaConta({ banco: '', agencia: '', conta: '', tipo: 'Conta Corrente', chavePix: '', tipoCadastro: '' });
                    setErrosNovaConta({});
                  }}
                  disabled={adicionandoConta}
                  className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdicionarConta}
                  disabled={adicionandoConta}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2 disabled:opacity-80 disabled:cursor-not-allowed"
                >
                  {adicionandoConta ? (
                    <>
                      <svg className="animate-spin w-4 h-4 text-white flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                      </svg>
                      Salvando...
                    </>
                  ) : (
                    'Adicionar Conta'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Câmera */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">
                Capturar {cameraDocType?.tipo.toUpperCase()} {cameraDocType?.lado && `- ${cameraDocType.lado}`}
              </h3>
              <button
                onClick={closeCameraModal}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '4/3' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeCameraModal}
                className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={capturePhoto}
                className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-camera-line text-xl"></i>
                Capturar Foto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso - Telefone Alterado */}
      {showTelefoneSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl text-center">
            {/* Ícone de Sucesso Animado */}
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>

            {/* Título */}
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Telefone Alterado!
            </h3>

            {/* Mensagem */}
            <p className="text-base text-slate-600 mb-6">
              Seu telefone foi atualizado com sucesso. Agora você receberá notificações e códigos de verificação no novo número.
            </p>

            {/* Novo Telefone */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
              <p className="text-xs text-green-700 font-semibold mb-2">Novo Telefone</p>
              <p className="text-lg font-bold text-green-900 flex items-center justify-center gap-2">
                <i className="ri-phone-line"></i>
                {dadosPessoais.telefone}
              </p>
            </div>

            {/* Botão de Fechar */}
            <button
              onClick={() => setShowTelefoneSuccessModal(false)}
              className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {/* Toast de Sucesso - Conta Removida */}
      {showRemocaoSucesso && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-[slideUp_0.4s_ease-out]">
          <div className="flex items-center gap-3 bg-slate-900 text-white px-5 py-3.5 rounded-2xl shadow-2xl min-w-[280px]">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-sm font-semibold">Conta removida com sucesso</p>
              <p className="text-xs text-slate-400">A conta bancária foi excluída do seu perfil.</p>
            </div>
            <button
              onClick={() => setShowRemocaoSucesso(false)}
              className="ml-2 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white transition-colors flex-shrink-0"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Toast de Sucesso - Conta Cadastrada */}
      {showCadastroSucesso && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-[slideUp_0.4s_ease-out]">
          <div className="flex items-center gap-3 bg-slate-900 text-white px-5 py-3.5 rounded-2xl shadow-2xl min-w-[280px]">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-bank-card-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-sm font-semibold">Conta cadastrada com sucesso</p>
              <p className="text-xs text-slate-400">A nova conta bancária foi adicionada ao seu perfil.</p>
            </div>
            <button
              onClick={() => setShowCadastroSucesso(false)}
              className="ml-2 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white transition-colors flex-shrink-0"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modal de Confirmação - Remover Conta */}
      {showRemoverContaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-delete-bin-line text-3xl text-red-500"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Remover Conta</h3>
              <p className="text-sm text-slate-600">
                Tem certeza que deseja remover esta conta bancária? Esta ação não poderá ser desfeita.
              </p>
            </div>

            {(() => {
              const conta = contas.find(c => c.id === contaParaRemover);
              if (!conta) return null;
              const isDadosBancarios = conta.tipo !== 'Conta PIX';
              return (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${isDadosBancarios ? 'ri-bank-line' : 'ri-exchange-funds-line'} text-slate-500`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">
                        {conta.banco !== '-' ? conta.banco : 'Conta PIX'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {isDadosBancarios ? `Ag. ${conta.agencia} · Conta ${conta.conta}` : `PIX · ${conta.tipoPix}: ${conta.chavePix}`}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRemoverContaModal(false);
                  setContaParaRemover(null);
                }}
                disabled={removendoConta}
                className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarRemocao}
                disabled={removendoConta}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors whitespace-nowrap text-sm flex items-center justify-center gap-2 disabled:opacity-80 disabled:cursor-not-allowed"
              >
                {removendoConta ? (
                  <>
                    <svg className="animate-spin w-4 h-4 text-white flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    Removendo...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Remover
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />

      {/* Modal Foto de Perfil */}
      <FotoPerfilModal
        isOpen={showFotoModal}
        onClose={() => setShowFotoModal(false)}
        fotoAtual={fotoAtual}
        nome={participante?.nome || ''}
        onSalvar={(novaFoto) => {
          setFotoAtual(novaFoto);
          toast.success('Foto atualizada', 'Sua foto de perfil foi alterada com sucesso.');
        }}
      />

      {/* Modal Alterar Senha */}
      <AlterarSenhaModal
        isOpen={showAlterarSenhaModal}
        onClose={() => setShowAlterarSenhaModal(false)}
        onSucesso={() => toast.success('Senha alterada', 'Sua senha foi atualizada com sucesso.')}
      />
    </div>
  );
}